// @flow

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Textarea from 'react-textarea-autosize'
import update from 'immutability-helper'
import { filter, isEqual } from 'lodash-es'
import type { Node } from 'react'

import Button from '../Button'
import Modal from '../Modal'
import ModalBody from '../Modal/ModalBody'
import ModalButtons from '../Modal/ModalButtons'
import ModalCloseButton from '../Modal/ModalCloseButton'
import ModalHead from '../Modal/ModalHead'
import ModalRow from '../Modal/ModalRow'
import { useOverflow } from '../../hooks/useOverflow'
import ConfigListItem from './ConfigListItem'
import ConfigList from './ConfigList'

import styles from './ConfigItem.module.scss'

import { CONFIG_PAGE_SIZE } from '../../constants'

type Props = {
  addListItemPlaceholder: string,
  addListItemTitle: string,
  createApi: Function,
  errorTxt?: string,
  listApi: Function,
  listTitle: string,
  maxValueLength?: number,
  modalTitle: string,
  onClose: Function,
}

const ConfigItemModal = (props: Props): Node => {
  const {
    addListItemPlaceholder,
    addListItemTitle,
    listApi,
    createApi,
    listTitle,
    maxValueLength,
    modalTitle,
  } = props
  const { t } = useTranslation('Common')

  const [list, setList] = useState([])
  const [initialList, setInitialList] = useState([])
  const [value, setValue] = useState('')
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState([])
  const [editing, setEditing] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initiated, setInitiated] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    listApi({ page, page_size: CONFIG_PAGE_SIZE }).then(data => {
      setList([...list, ...data.results.objects])
      setInitialList([...initialList, ...data.results.objects])
      setMeta(data.meta)
      setLoading(false)

      if (!initiated) {
        setInitiated(true)
      }
    })
  }, [page])

  useOverflow()

  const checkLength = create => {
    if (!maxValueLength) {
      return true
    }

    let isValid = create.every(s => s.length <= maxValueLength)

    if (!isValid) {
      setError(t('MaxValueExceeded', { length: maxValueLength }))
    } else {
      setError(null)
    }

    return isValid
  }

  const getEqualValue = value => value.trim().toLowerCase()

  const checkDuplicate = create => {
    let isValid = false
    const currentList = create.map(value => getEqualValue(value))

    const newList = list?.map(item =>
      currentList.includes(getEqualValue(item.value))
        ? { ...item, isDuplicate: true }
        : { ...item, isDuplicate: false }
    )

    isValid = !newList.some(({ isDuplicate }) => isDuplicate)

    if (!isValid) {
      setError(t('DuplicateNotAllowed'))
    } else {
      setError(null)
    }

    return isValid
  }

  const handleChangeText = e => setValue(e.target.value)

  const handleReplace = (id, targetIndex) => {
    const from = list.findIndex(i => i.id === id)

    if (from === targetIndex) {
      return
    }

    const proirity = list[from]

    const updated = update(list, {
      $splice: [
        [from, 1],
        [targetIndex, 0, proirity],
      ],
    })

    setList(updated.map((u, i) => ({ ...u, position: i + 1 })))
  }

  if (!initiated) {
    return null
  }

  const hasChanges = () => {
    if (initialList.length !== list.length || value) {
      return true
    }

    let changed = filter(
      list,
      (item, index) => !isEqual(item, initialList[index])
    )

    return !!changed.length
  }

  const handleClose = () => {
    if (hasChanges()) {
      setList(initialList)
      setValue('')
    } else {
      props.onClose()
    }
  }

  const handleSubmit = () => {
    const create = value
      ? value
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean)
      : []

    if (!checkDuplicate(create) || !checkLength(create)) {
      return
    }

    createApi({ create, update: list }).finally(() => props.onClose())
  }

  const handleRemove = id => {
    setList(list.filter(roomType => roomType.id !== id))
    setEditing(null)
  }

  const handleSave = (item, index) => {
    setList(update(list, { [index]: { $set: item } }))
    setEditing(null)
  }

  const handleScroll = e => {
    if (loading || (meta && meta.curr_page === meta.page_count)) {
      return
    }

    const { scrollHeight, offsetHeight, scrollTop } = e.target
    const scrolled = offsetHeight + scrollTop

    if (scrollHeight / 2 <= scrolled) {
      setPage(meta ? meta.curr_page + 1 : 1)
    }
  }

  return (
    <Modal isOpen className={styles.modal} onRequestClose={props.onClose}>
      <ModalHead title={modalTitle} />
      <ModalCloseButton onClose={props.onClose} />
      <ModalBody>
        {list && !!list.length && (
          <ModalRow>
            <div className={styles.form}>
              <span className={styles.title}>{listTitle}</span>
              <ConfigList onScroll={handleScroll}>
                {list.map((item, index) => (
                  <ConfigListItem
                    index={index}
                    isEditing={editing === item.id}
                    item={item}
                    key={item.id}
                    maxValueLength={maxValueLength}
                    onRemove={handleRemove}
                    onReplace={handleReplace}
                    onSave={handleSave}
                    onEdit={setEditing}
                  />
                ))}
              </ConfigList>
            </div>
          </ModalRow>
        )}
        <ModalRow>
          <div className={styles.form}>
            <span className={styles.title}>{addListItemTitle}</span>
            <Textarea
              autoFocus
              maxRows={5}
              className={styles.textarea}
              value={value}
              placeholder={addListItemPlaceholder}
              onChange={handleChangeText}
            />
            <div className='input--error input--large'>
              <div className='input__msg'>{error}</div>
            </div>
          </div>
        </ModalRow>
      </ModalBody>
      <ModalButtons>
        <Button.Save disabled={!hasChanges()} onClick={handleSubmit}>
          {t('Save')}
        </Button.Save>
        <Button.Cancel onClick={handleClose}>{t('Cancel')}</Button.Cancel>
      </ModalButtons>
    </Modal>
  )
}

export default ConfigItemModal
